// ABOUTME: SPEC-53 — the hover magnifier lens of the 2D grid's product view (App.js `handleLensMove`
// ABOUTME: + its lens render), extracted as a hook so the collection piece card behaves identically.
//
// The grid's own inline copy in App.js is deliberately left untouched (task rule: do not change the
// grid tenants' behaviour); the numbers here are its numbers — a 220px round lens showing the image
// at 250%, positioned on the pointer, with the background offset as a percentage of the container.
//
// Two gates the grid never needed (headless run 2026-09-09: the lens sat at rest, top-left of the
// image, on a touch context with no pointer over it):
//   • the lens exists only on a hover-capable, fine-pointer, non-touch device (deviceProfile.js —
//     an iPad or a touchscreen laptop has no hover to follow, and a tap would leave it stuck);
//   • it shows only once a real `mousemove` has placed it — `mouseenter` alone (which Chrome also
//     synthesises when an element appears under a stationary cursor) reveals nothing.

import { useCallback, useRef, useState } from 'react';
import { useDeviceProfile } from '../Projectionist/useViewportSize';

export const LENS_PX = 220;
export const LENS_ZOOM = '250%';

/** Can this device follow a hover at all? Pure, exported for the tests. */
export function lensCapable(profile) {
  if (!profile) return true;
  return !(profile.noHover || profile.coarsePointer || profile.touch);
}

/**
 * @returns {{
 *   containerRef: {current: HTMLElement|null},
 *   visible: boolean,
 *   pos: {x: number, y: number, bgX: number, bgY: number},
 *   handlers: { onMouseEnter: () => void, onMouseLeave: () => void, onMouseMove: (e: MouseEvent) => void },
 *   lensStyle: (imageUrl: string) => object,
 * }}
 */
export function useMagnifierLens() {
  const containerRef = useRef(null);
  const capable = lensCapable(useDeviceProfile());
  const [hovering, setHovering] = useState(false);
  const [pos, setPos] = useState(null); // null until a real move has placed the lens

  const onMouseMove = useCallback((e) => {
    const el = containerRef.current;
    if (!el) return;
    const { left, top, width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    // Pointer relative to the container; the same fraction drives the zoomed background.
    const x = e.clientX - left;
    const y = e.clientY - top;
    setPos({ x, y, bgX: (x / width) * 100, bgY: (y / height) * 100 });
  }, []);

  const onMouseEnter = useCallback(() => setHovering(true), []);
  const onMouseLeave = useCallback(() => { setHovering(false); setPos(null); }, []);

  const at = pos || { x: 0, y: 0, bgX: 0, bgY: 0 };
  const lensStyle = useCallback((imageUrl) => ({
    position: 'absolute',
    pointerEvents: 'none',
    width: `${LENS_PX}px`,
    height: `${LENS_PX}px`,
    left: `${at.x - LENS_PX / 2}px`,
    top: `${at.y - LENS_PX / 2}px`,
    borderRadius: '50%',
    border: '1px solid rgba(0,0,0,0.1)',
    boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
    background: '#fff',
    backgroundImage: `url("${imageUrl}")`,
    backgroundPosition: `${at.bgX}% ${at.bgY}%`,
    backgroundSize: LENS_ZOOM,
    backgroundRepeat: 'no-repeat',
    zIndex: 40,
  }), [at.x, at.y, at.bgX, at.bgY]);

  const handlers = capable ? { onMouseEnter, onMouseLeave, onMouseMove } : {};
  return { containerRef, capable, visible: capable && hovering && pos !== null, pos: at, handlers, lensStyle };
}

export default useMagnifierLens;
